import { numberEnum } from '@server/shared/utils/enum'
import { z } from 'zod'

export const paymentTypeSchema = z.enum([
  'TRIAL',
  'TRIAL_FREE',
  'SUBSCRIBE',
  'LUMP_SUM',
  'IPAD',
  'EXTEND',
  'BUSINESS',
  'SMART_TALK',
  'SMART_TALK_TRIAL',
])

/** LIGHTSUBSCRIBE: 라이트 월 8회 구독(시크릿 링크 전용 /subscribes/tickets/light) */
export const promotionTypeSchema = z.enum(['NONE', 'PAYBACK', 'LIGHTSUBSCRIBE'])

export const paymentVendorSchema = z.enum(['KAKAO', 'CARD', 'NAVER', 'FREE'])
export const paymentMessageBodyTypeSchema = z.enum([
  'TRIAL',
  'TRIAL_FREE',
  'FIRST_BILLING',
  'LUMP_SUM',
  'BILLING',
  'CANCEL',
  'BEHIND',
])
export const trialLevelSchema = z.enum(['B', 'C1', 'C2', 'D'])

export type PaymentType = z.infer<typeof paymentTypeSchema>
export type PromotionType = z.infer<typeof promotionTypeSchema>

export const langTypeSchema = z.enum(['EN', 'JP', 'ENJP'])

/** 실제 1:1 수업에 저장되는 언어. ENJP는 상품 묶음 값이므로 개별 수업에는 사용하지 않는다. */
export const classLangTypeSchema = z.enum(['EN', 'JP', 'KR'])

/** 수강권·결제 상품에 저장되는 언어. ENJP 더블팩과 JP 시장의 한국어 수업을 모두 포함한다. */
export const ticketLangTypeSchema = z.enum(['EN', 'JP', 'KR', 'ENJP'])

export type PaymentVendorType = z.infer<typeof paymentVendorSchema>
export type PaymentMessageBodyType = z.infer<typeof paymentMessageBodyTypeSchema>
export type TrialLevel = z.infer<typeof trialLevelSchema>

export type LangType = z.infer<typeof langTypeSchema>
export type ClassLangType = z.infer<typeof classLangTypeSchema>
export type TicketLangType = z.infer<typeof ticketLangTypeSchema>

export const vendorSchema = z.enum(['CARD', 'KAKAO', 'NAVER', 'FREE'])

export type Vendor = z.infer<typeof vendorSchema>

export const yesNoSchema = z.enum(['Y', 'N'])

export type YesNo = z.infer<typeof yesNoSchema>

export const lessonCountPerMonthSchema = numberEnum([8, 12, 999])

export type LessonCountPerMonth = z.infer<typeof lessonCountPerMonthSchema>

/** 정규 레슨 상품 선택지. JP 한국어 55분 체험레슨은 classLessonTimeSchema를 사용한다. */
export const lessonTimeSchema = numberEnum([15, 25])

/** 실제 수업·수강권에 저장될 수 있는 전체 수업 시간. */
export const classLessonTimeSchema = numberEnum([15, 25, 55])

export type LessonTime = z.infer<typeof lessonTimeSchema>
export type ClassLessonTime = z.infer<typeof classLessonTimeSchema>

export const lessonMonthSchema = numberEnum([3, 6, 12])

export type LessonMonth = z.infer<typeof lessonMonthSchema>

export const curriculumTypeSchema = z.enum(['TRIAL', 'TRIAL_1', 'TRIAL_2', 'RECENT', 'BUSINESS', 'BASIC', 'SMART_TALK'])

export type CurriculumType = z.infer<typeof curriculumTypeSchema>

export const aiCharacterChatDifficultyTypeSchema = z.enum(['ROLE_PLAYING', 'ARTIFICIAL_TUTOR', 'EXAM'])

export type AiCharacterChatDifficultyType = z.infer<typeof aiCharacterChatDifficultyTypeSchema>

export const characterChatTypeSchema = z.enum(['ROLE_PLAYING', 'ARTIFICIAL_TUTOR', 'EXAM'])

export type CharacterChatType = z.infer<typeof characterChatTypeSchema>

export const createLegacyBaseResponseSchema = <T extends z.ZodType>(dataSchema: T) => {
  return z.object({
    resultCd: z.string().transform((val) => Number(val)),
    resultMsg: z.string().optional(),
    result: dataSchema,
  })
}
export const createBaseResponseSchema = <T extends z.ZodType>(dataSchema: T) => {
  return z.object({
    resultCd: z.number(),
    resultCdName: z.string(),
    message: z.string().nullable(),
    data: dataSchema,
  })
}

export const createBaseErrorResponseSchema = <StatusCode extends number, ResultCdName extends string>(
  statusCode: StatusCode,
  resultCdName: ResultCdName,
) => {
  return z.object({
    resultCd: z.literal(z.number().min(400).max(599).parse(statusCode)),
    resultCdName: z.enum([resultCdName]),
    message: z.string(),
    data: z.null(),
  })
}

export type LegacyBaseResponseSchema<T = unknown> = {
  resultCd: number
  result: T
}

export type BaseResponseSchema<T = unknown, ResultCdName extends string = string, Message = string | null> = {
  resultCd: number
  resultCdName: ResultCdName
  message: Message
  data: T
}
